import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { RefreshCw, Settings, Trash2, CheckCircle, AlertCircle, Link2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OTA_CONFIG = {
  booking_com: {
    name: 'Booking.com',
    logo: '🏨',
    color: 'from-blue-600 to-blue-700',
    fields: [
      { key: 'client_id', label: 'Hotel ID', placeholder: '12345678' },
      { key: 'client_secret', label: 'API Key', placeholder: 'your-api-key', type: 'password' },
      { key: 'hotel_id', label: 'Property ID', placeholder: '98765' }
    ],
    instructions: 'Get credentials from Booking.com Partner Hub → Connectivity → API Settings'
  },
  expedia: {
    name: 'Expedia',
    logo: '✈️',
    color: 'from-yellow-600 to-yellow-700',
    fields: [
      { key: 'client_id', label: 'EPC Username', placeholder: 'epc_username' },
      { key: 'client_secret', label: 'EPC Password', placeholder: 'password', type: 'password' },
      { key: 'hotel_id', label: 'Hotel ID', placeholder: '12345' }
    ],
    instructions: 'Get credentials from Expedia Partner Central → Channel Manager API'
  },
  airbnb: {
    name: 'Airbnb',
    logo: '🏠',
    color: 'from-pink-600 to-pink-700',
    fields: [
      { key: 'client_id', label: 'Client ID', placeholder: 'your-client-id' },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'your-secret', type: 'password' },
      { key: 'hotel_id', label: 'Listing ID', placeholder: 'listing-id' }
    ],
    instructions: 'Get credentials from Airbnb Professional Hosting Tools → API Access'
  },
  hotels_com: {
    name: 'Hotels.com',
    logo: '🏩',
    color: 'from-red-600 to-red-700',
    fields: [
      { key: 'client_id', label: 'EPC Username', placeholder: 'epc_username' },
      { key: 'client_secret', label: 'EPC Password', placeholder: 'password', type: 'password' },
      { key: 'hotel_id', label: 'Hotel ID', placeholder: '12345' }
    ],
    instructions: 'Uses same credentials as Expedia (part of Expedia Group)'
  },
  agoda: {
    name: 'Agoda',
    logo: '🌏',
    color: 'from-purple-600 to-purple-700',
    fields: [
      { key: 'client_id', label: 'Hotel Code', placeholder: 'hotel-code' },
      { key: 'client_secret', label: 'YCS Password', placeholder: 'password', type: 'password' },
      { key: 'hotel_id', label: 'Property ID', placeholder: '12345' }
    ],
    instructions: 'Get credentials from Agoda YCS (Yield Management System)'
  }
};

const ChannelManager = () => {
  const { selectedProperty } = useOutletContext();
  const [channels, setChannels] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  
  // Modals
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  
  // Selected data
  const [selectedOTA, setSelectedOTA] = useState('');
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [mappings, setMappings] = useState([]);
  const [events, setEvents] = useState([]);
  
  // Mapping form
  const [newMapping, setNewMapping] = useState({ pms_unit_type_id: '', ota_room_id: '', ota_room_name: '' });

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [channelsRes, typesRes] = await Promise.all([
        axios.get(`${API}/channel-manager/properties/${selectedProperty.id}/channels`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/properties/${selectedProperty.id}/unit-types`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setChannels(channelsRes.data);
      setUnitTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load channel data');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectOTA = async () => {
    if (!selectedOTA) {
      toast.error('Please select an OTA');
      return;
    }

    const otaConfig = OTA_CONFIG[selectedOTA];
    const missingFields = otaConfig.fields.filter(f => !credentials[f.key]);
    
    if (missingFields.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/channel-manager/properties/${selectedProperty.id}/channels`,
        {
          ota_name: selectedOTA,
          credentials,
          is_active: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Connected to ${otaConfig.name}`);
      setShowConnectModal(false);
      setSelectedOTA('');
      setCredentials({});
      fetchData();
    } catch (error) {
      console.error('Error connecting OTA:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect OTA');
    }
  };

  const handleDisconnect = async (channelId) => {
    if (!window.confirm('Are you sure you want to disconnect this channel?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/channel-manager/channels/${channelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Channel disconnected');
      fetchData();
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect channel');
    }
  };

  const handleSync = async (channel, syncType = 'all') => {
    setSyncing(channel.id);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/channel-manager/channels/${channel.id}/sync`,
        { sync_type: syncType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const openMappingModal = async (channel) => {
    setSelectedChannel(channel);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/channel-manager/channels/${channel.id}/mappings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMappings(response.data);
      setShowMappingModal(true);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      toast.error('Failed to load mappings');
    }
  };

  const handleSaveMapping = async () => {
    if (!newMapping.pms_unit_type_id || !newMapping.ota_room_id) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/channel-manager/channels/${selectedChannel.id}/mappings`,
        newMapping,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Room mapping saved');
      setNewMapping({ pms_unit_type_id: '', ota_room_id: '', ota_room_name: '' });
      
      // Refresh mappings
      const response = await axios.get(
        `${API}/channel-manager/channels/${selectedChannel.id}/mappings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMappings(response.data);
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error('Failed to save mapping');
    }
  };

  const viewEvents = async (channel) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/channel-manager/channels/${channel.id}/events`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvents(response.data);
      setSelectedChannel(channel);
      setShowEventsModal(true);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  };

  const availableOTAs = Object.keys(OTA_CONFIG).filter(
    ota => !channels.find(ch => ch.ota_name === ota)
  );

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            Channel Manager
          </h1>
          <p className="text-slate-600 mt-2">
            Connect and sync with online travel agencies (OTAs)
          </p>
          <Badge variant="outline" className="mt-2">Beta - Coming Soon</Badge>
        </div>
        {availableOTAs.length > 0 && (
          <Button onClick={() => setShowConnectModal(true)} className="gap-2">
            <Link2 size={18} />
            Connect Channel
          </Button>
        )}
      </div>

      {/* Connected Channels */}
      {loading ? (
        <p className="text-center py-8">Loading channels...</p>
      ) : channels.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.map((channel) => {
            const config = OTA_CONFIG[channel.ota_name] || {};
            return (
              <Card key={channel.id} className="border-2">
                <CardHeader className={`bg-gradient-to-r ${config.color} text-white`}>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="text-2xl">{config.logo}</span>
                      {config.name}
                    </span>
                    {channel.is_verified ? (
                      <CheckCircle className="text-green-200" size={20} />
                    ) : (
                      <AlertCircle className="text-orange-200" size={20} />
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                  <div className="text-sm space-y-1">
                    <p className="text-slate-600">
                      <strong>Hotel ID:</strong> {channel.hotel_id || 'Not set'}
                    </p>
                    <p className="text-slate-600">
                      <strong>Last Sync:</strong> {channel.last_sync ? new Date(channel.last_sync).toLocaleString() : 'Never'}
                    </p>
                    <p className="text-slate-600">
                      <strong>Status:</strong>{' '}
                      {channel.is_active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Button 
                      onClick={() => handleSync(channel)}
                      disabled={syncing === channel.id}
                      variant="outline"
                      className="w-full gap-2"
                    >
                      <RefreshCw className={syncing === channel.id ? 'animate-spin' : ''} size={16} />
                      {syncing === channel.id ? 'Syncing...' : 'Sync Now'}
                    </Button>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        onClick={() => openMappingModal(channel)}
                        variant="outline"
                        size="sm"
                        className="gap-1"
                      >
                        <MapPin size={14} />
                        Map Rooms
                      </Button>
                      <Button 
                        onClick={() => viewEvents(channel)}
                        variant="outline"
                        size="sm"
                        className="gap-1"
                      >
                        <Settings size={14} />
                        Events
                      </Button>
                    </div>
                    
                    <Button 
                      onClick={() => handleDisconnect(channel.id)}
                      variant="destructive"
                      size="sm"
                      className="w-full gap-2"
                    >
                      <Trash2 size={14} />
                      Disconnect
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Link2 className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-600 mb-2">No channels connected yet</p>
            <p className="text-sm text-slate-500 mb-4">
              Connect to OTAs to sync inventory and receive bookings
            </p>
            {availableOTAs.length > 0 && (
              <Button onClick={() => setShowConnectModal(true)}>
                Connect Your First Channel
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Connect Channel Modal */}
      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Connect OTA Channel</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {!selectedOTA ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Select an OTA to connect:</p>
                {availableOTAs.map((otaKey) => {
                  const config = OTA_CONFIG[otaKey];
                  return (
                    <Button
                      key={otaKey}
                      onClick={() => setSelectedOTA(otaKey)}
                      variant="outline"
                      className="w-full justify-start gap-3 h-auto p-4"
                    >
                      <span className="text-2xl">{config.logo}</span>
                      <div className="text-left">
                        <p className="font-medium">{config.name}</p>
                        <p className="text-xs text-slate-500">Click to configure</p>
                      </div>
                    </Button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-4">
                <div className={`p-4 bg-gradient-to-r ${OTA_CONFIG[selectedOTA].color} text-white rounded-lg`}>
                  <p className="flex items-center gap-2 font-medium">
                    <span className="text-2xl">{OTA_CONFIG[selectedOTA].logo}</span>
                    {OTA_CONFIG[selectedOTA].name}
                  </p>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-900">
                    <strong>How to get credentials:</strong><br />
                    {OTA_CONFIG[selectedOTA].instructions}
                  </p>
                </div>

                {OTA_CONFIG[selectedOTA].fields.map((field) => (
                  <div key={field.key}>
                    <Label>{field.label} *</Label>
                    <Input
                      type={field.type || 'text'}
                      value={credentials[field.key] || ''}
                      onChange={(e) => setCredentials(prev => ({
                        ...prev,
                        [field.key]: e.target.value
                      }))}
                      placeholder={field.placeholder}
                    />
                  </div>
                ))}

                <div className="flex gap-2">
                  <Button onClick={handleConnectOTA} className="flex-1">
                    Save & Connect
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSelectedOTA('');
                      setCredentials({});
                    }}
                  >
                    Back
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Mapping Modal */}
      <Dialog open={showMappingModal} onOpenChange={setShowMappingModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Room Type Mapping - {selectedChannel && OTA_CONFIG[selectedChannel.ota_name]?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-sm text-slate-700">
                Map your PMS room types to {selectedChannel && OTA_CONFIG[selectedChannel.ota_name]?.name} room IDs.
                This ensures availability and rates sync correctly.
              </p>
            </div>

            {/* Existing Mappings */}
            {mappings.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Current Mappings:</p>
                <div className="space-y-2">
                  {mappings.map((mapping) => (
                    <div key={mapping.id} className="p-3 border rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-medium">{mapping.pms_unit_type_name}</p>
                        <p className="text-sm text-slate-600">
                          OTA Room ID: {mapping.ota_room_id}
                          {mapping.ota_room_name && ` (${mapping.ota_room_name})`}
                        </p>
                      </div>
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add New Mapping */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Add New Mapping:</p>
              <div className="space-y-3">
                <div>
                  <Label>PMS Room Type</Label>
                  <select
                    value={newMapping.pms_unit_type_id}
                    onChange={(e) => setNewMapping({...newMapping, pms_unit_type_id: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  >
                    <option value="">Select room type</option>
                    {unitTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>OTA Room ID *</Label>
                  <Input
                    value={newMapping.ota_room_id}
                    onChange={(e) => setNewMapping({...newMapping, ota_room_id: e.target.value})}
                    placeholder="e.g., 12345"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Find this ID in your {selectedChannel && OTA_CONFIG[selectedChannel.ota_name]?.name} dashboard
                  </p>
                </div>

                <div>
                  <Label>OTA Room Name (optional)</Label>
                  <Input
                    value={newMapping.ota_room_name}
                    onChange={(e) => setNewMapping({...newMapping, ota_room_name: e.target.value})}
                    placeholder="e.g., Deluxe King Room"
                  />
                </div>

                <Button onClick={handleSaveMapping} className="w-full">
                  Save Mapping
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Events/Logs Modal */}
      <Dialog open={showEventsModal} onOpenChange={setShowEventsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sync Events - {selectedChannel && OTA_CONFIG[selectedChannel.ota_name]?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {events.length > 0 ? (
              events.map((event) => (
                <div key={event.id} className="p-3 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{event.event_type}</p>
                      <p className="text-xs text-slate-600">{event.message}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(event.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant={event.status === 'SUCCESS' ? 'default' : 'destructive'}>
                      {event.status}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center py-8 text-slate-600">No events yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChannelManager;
